import { useMemo } from "react";
import { useTheme } from "@/hooks/useTheme";
import { OpArtBackground } from "@/components/main-page/OpArtBackground";
import { WaveBackground } from "@/components/main-page/WaveBackground";

// Seeded PRNG for deterministic confetti
function mulberry32(seed: number) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Particle {
  x: number; y: number; size: number; rotation: number;
  color: string; opacity: number; shape: "rect" | "circle" | "line";
}

function generateParticles(count: number, w: number, h: number): Particle[] {
  const rng = mulberry32(42);
  const colors = ["#6366f1", "#818cf8", "#a5b4fc", "#4f46e5", "#c4b5fd", "#fbbf24", "#34d399"];
  const shapes: Particle["shape"][] = ["rect", "circle", "line"];
  return Array.from({ length: count }, () => ({
    x: rng() * w, y: rng() * h,
    size: 3 + rng() * 8, rotation: rng() * 360,
    color: colors[Math.floor(rng() * colors.length)]!,
    opacity: 0.15 + rng() * 0.35,
    shape: shapes[Math.floor(rng() * shapes.length)]!,
  }));
}

function ConfettiLayer({ width, height }: { width: number; height: number }) {
  const particles = useMemo(() => generateParticles(Math.round(width * height / 12000), width, height), [width, height]);
  return (
    <svg style={{ position: "absolute", inset: 0 }} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {particles.map((p, i) => {
        if (p.shape === "circle") return <circle key={i} cx={p.x} cy={p.y} r={p.size / 2} fill={p.color} opacity={p.opacity} />;
        if (p.shape === "line") {
          const len = p.size * 2, rad = (p.rotation * Math.PI) / 180;
          return <line key={i} x1={p.x} y1={p.y} x2={p.x + Math.cos(rad) * len} y2={p.y + Math.sin(rad) * len} stroke={p.color} strokeWidth={1.5} opacity={p.opacity} strokeLinecap="round" />;
        }
        return <rect key={i} x={p.x} y={p.y} width={p.size} height={p.size * 0.6} rx={1} fill={p.color} opacity={p.opacity} transform={`rotate(${p.rotation} ${p.x + p.size / 2} ${p.y + p.size * 0.3})`} />;
      })}
    </svg>
  );
}

export function SocialBackgroundPage() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const style = params.get("style") || "circles"; // circles | confetti | wave
  const w = Number(params.get("w")) || 1200;
  const h = Number(params.get("h")) || 627;

  return (
    <div
      data-ready="true"
      style={{
        width: w,
        height: h,
        background: isDark ? "#0a0a0a" : "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", inset: 0, opacity: 0.6 }}>
        {style === "circles" && <OpArtBackground scrollProgress={0} />}
        {style === "wave" && <WaveBackground scrollProgress={0} />}
        {style === "confetti" && <WaveBackground scrollProgress={0} />}
      </div>
      <div style={{ position: "absolute", inset: 0 }}>
        {style === "confetti" && <ConfettiLayer width={w} height={h} />}
      </div>
    </div>
  );
}
